import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import css from './ChatPurchaseModal.module.css';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import Modal from '../Modal/Modal';
import { Button, SecondaryButton, Icons } from '../';
import EnhancedPaymentMethodsForm from '../../containers/PaymentMethodsPage/PaymentMethodsForm/PaymentMethodsForm';
import { userSelector } from '../../ducks/user.duck';
import {
  getClientSecret,
  getDefaultPaymentMethod,
  getPaymentParams,
  getUserFullName,
} from '../../util/userDataExtraction';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useConfiguration } from '../../context/configurationContext';
import { ensureCurrentUser } from '../../util/data';
import { createStripeSetupIntent } from '../../containers/PaymentMethodsPage/PaymentMethodsPage.duck';
import {
  directChatCreateStripePaymentIntent,
  handleCardSetup,
  handleCardSetupErrorSelector,
} from '../../ducks/stripe.duck';
import { paymentMethodsSelector, savePaymentMethod } from '../../ducks/paymentMethods.duck';
import { showErrorToast } from '../../util/toast';
import { convertToCents } from '../../util/genericHelpers';
import { ENTER } from '../../util/constants';
import { ChatPurchaseModalSteps } from '../../util/enums';
import { createResourceLocatorString } from '../../util/routes';

const ChatPurchaseModal = ({
  isOpen,
  onClose,
  puchaseModalState,
  setIsConfirmationModalOpen = () => {},
  setChatPurchaseModalState,
  authorDisplayName,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const intl = useIntl();

  const currentUser = useSelector(userSelector);
  const {
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
  } = useSelector(paymentMethodsSelector);
  const handleCardSetupError = useSelector(handleCardSetupErrorSelector);

  const userFullName = getUserFullName(currentUser);
  const hasDefaultPaymentMethod = getDefaultPaymentMethod(currentUser);

  const [currentStep, setCurrentStep] = useState(ChatPurchaseModalSteps.REVIEW);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extract price information
  const priceAmount = puchaseModalState?.priceAmount || 0;
  const currency = config?.currency || 'USD';
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(priceAmount);
  // Navigation handlers
  const goToPaymentStep = useCallback(() => {
    setCurrentStep(ChatPurchaseModalSteps.PAYMENT);
  }, []);

  const goToReviewStep = useCallback(() => {
    setCurrentStep(ChatPurchaseModalSteps.REVIEW);
  }, []);

  // Reset modal state
  const resetModalState = useCallback(() => {
    setCurrentStep(ChatPurchaseModalSteps.REVIEW);
    setIsSubmitting(false);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    onClose();
    resetModalState();
  }, [onClose, resetModalState]);

  const navigateToOrderDetails = orderId => {
    history.push(
      createResourceLocatorString('OrderDetailsPage', routeConfiguration, { id: orderId })
    );
  };
  // Handle payment submission
  const handleSubmit = useCallback(
    async params => {
      setIsSubmitting(true);

      try {
        const ensuredCurrentUser = ensureCurrentUser(currentUser);
        const currentUserId = ensuredCurrentUser?.id?.uuid;
        const stripeCustomer = ensuredCurrentUser.stripeCustomer;
        const { stripe, card, formValues } = params;
        const customerId = stripeCustomer?.attributes?.stripeCustomerId;
        let paymentMethodId =
          stripeCustomer?.defaultPaymentMethod?.attributes?.stripePaymentMethodId;

        if (!stripeCustomer) {
          const setupIntent = await dispatch(createStripeSetupIntent(params));
          const setupIntentClientSecret = getClientSecret(setupIntent);
          const paymentParams = getPaymentParams(currentUser, formValues);

          // Handle card setup
          const stripeParams = { stripe, card, setupIntentClientSecret, paymentParams };
          const result = await dispatch(handleCardSetup(stripeParams));
          const newPaymentMethod = result.setupIntent.payment_method;

          paymentMethodId = newPaymentMethod?.id;
          // Save the new payment method
          await dispatch(savePaymentMethod(stripeCustomer, newPaymentMethod));
        }

        const paymentPayload = {
          customerId: customerId,
          amount: convertToCents(priceAmount),
          currency: currency,
          paymentMethodId: paymentMethodId,
        };

        await dispatch(
          directChatCreateStripePaymentIntent(paymentPayload, {
            ...puchaseModalState,
            navigateToOrderDetails,
          })
        );

        // Open confirmation modal
        setIsConfirmationModalOpen();
        handleModalClose();
      } catch (error) {
        showErrorToast(error?.message || 'Payment failed. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      currentUser,
      priceAmount,
      currency,
      puchaseModalState,
      dispatch,
      setIsConfirmationModalOpen,
      handleModalClose,
    ]
  );

  // Render back button for payment step
  const renderBackButton = useCallback(
    () => (
      <div
        className={css.goBackButton}
        onClick={goToReviewStep}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === ENTER && goToReviewStep()}
        aria-label={intl.formatMessage({ id: 'ChatPurchaseModal.goBack' })}
      >
        <Icons name="back" />
        {intl.formatMessage({ id: 'ChatPurchaseModal.goBack' })}
      </div>
    ),
    [goToReviewStep, intl]
  );

  // Memoized modal title
  const modalTitle = useMemo(() => {
    if (currentStep === ChatPurchaseModalSteps.REVIEW) {
      return <FormattedMessage id="ChatPurchaseModal.reviewTitle" />;
    }

    if (currentStep === ChatPurchaseModalSteps.PAYMENT) {
      return (
        <>
          {renderBackButton()}
          <FormattedMessage id="SubscriptionPaymentFormModal.paymentCardDetails" />
        </>
      );
    }

    return null;
  }, [currentStep, renderBackButton]);

  // Render review step content
  const renderReviewStep = () => (
    <div className={css.reviewStepContainer}>
      <div className={css.reviewContent}>
        {/* Main Heading */}
        <h2 className={css.reviewHeading}>
          <FormattedMessage
            id="ChatPurchaseModal.heading"
            values={{
              amount: formattedAmount,
              authorName: authorDisplayName || 'the author',
            }}
          />
        </h2>

        {/* Description */}
        <div className={css.reviewDescription}>
          <p>
            <FormattedMessage
              id="ChatPurchaseModal.description"
              values={{
                amount: formattedAmount,
                authorName: authorDisplayName || 'the author',
              }}
            />
          </p>
        </div>

        {/* Important Notice/Disclaimer */}
        <div className={css.disclaimerBox}>
          <div className={css.disclaimerIcon}>
            <Icons name="info" />
          </div>
          <div className={css.disclaimerContent}>
            <h3 className={css.disclaimerTitle}>
              <FormattedMessage id="ChatPurchaseModal.disclaimerTitle" />
            </h3>
            <ul className={css.disclaimerList}>
              <li>
                <FormattedMessage
                  id="ChatPurchaseModal.noChargeUntilReply"
                  values={{ authorName: authorDisplayName || 'the author' }}
                />
              </li>
              <li>
                <FormattedMessage id="ChatPurchaseModal.refundPolicy" />
              </li>
              <li>
                <FormattedMessage id="ChatPurchaseModal.paymentAuthorization" />
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className={css.buttonActions}>
        <SecondaryButton type="button" onClick={handleModalClose}>
          <FormattedMessage id="ChatPurchaseModal.cancel" />
        </SecondaryButton>
        <Button type="button" onClick={goToPaymentStep}>
          <FormattedMessage id="ChatPurchaseModal.next" />
        </Button>
      </div>
    </div>
  );

  // Render payment step content
  const renderPaymentStep = () => (
    <div className={css.paymentStepContainer}>
      <EnhancedPaymentMethodsForm
        className={css.paymentForm}
        formId="PaymentMethodsForm"
        initialValues={{ name: userFullName || '' }}
        onSubmit={handleSubmit}
        handleRemovePaymentMethod={{}}
        hasDefaultPaymentMethod={hasDefaultPaymentMethod}
        addPaymentMethodError={addPaymentMethodError}
        deletePaymentMethodError={deletePaymentMethodError}
        createStripeCustomerError={createStripeCustomerError}
        handleCardSetupError={handleCardSetupError}
        inProgress={isSubmitting}
        paymentMethodModalState={!!priceAmount}
        saveActionMessage={<FormattedMessage id="SubscriptionPage.createIntentBtn" />}
      />
    </div>
  );

  return (
    <div className={css.chatPurchaseModal}>
      <Modal
        id="ChatPurchaseModal"
        isOpen={isOpen}
        onClose={handleModalClose}
        usePortal
        contentClassName={css.modalContent}
        onManageDisableScrolling={(componentId, disableScrolling) =>
          dispatch(manageDisableScrolling(componentId, disableScrolling))
        }
        modalTitle={modalTitle}
        isEditListingSubscriptionModal
      >
        {currentStep === ChatPurchaseModalSteps.REVIEW && renderReviewStep()}
        {currentStep === ChatPurchaseModalSteps.PAYMENT && renderPaymentStep()}
      </Modal>
    </div>
  );
};

export default ChatPurchaseModal;
